import { v4 as uuidv4 } from 'uuid';
import { recordApiResponse } from '@/utils/apiRecorder';
import { use401Handler } from '@/composables/use401Handler';

interface RequestOptions {
    params?: Record<string, string>;
    body?: any;
    headers?: Record<string, string>;
}

export class HttpBaseClient {
    protected baseUrl: string;
    protected activeRequests: Set<AbortController> = new Set();

    constructor(baseUrl: string) {
        this.baseUrl = baseUrl;
    }
    
    /**
     * Cancel all active requests
     * Called during logout to prevent in-flight requests from interfering with session cleanup
     */
    public cancelAllRequests(): void {
        this.activeRequests.forEach(controller => {
            controller.abort();
        });
        this.activeRequests.clear();
    }

    protected async request<T>(
        method: string,
        path: string,
        options: RequestOptions = {},
        additionalHeaders: Record<string, string> = {}
    ): Promise<T> {
        // Build the query string if we have params
        let queryString = '';
        if (options.params) {
            const searchParams = new URLSearchParams();
            Object.entries(options.params).forEach(([key, value]) => {
                searchParams.append(key, value);
            });
            queryString = `?${searchParams.toString()}`;
        }

        let reqBody = options.body || undefined;

        // Special handling for URLSearchParams - don't stringify these
        const isFormData = reqBody instanceof URLSearchParams;

        if (reqBody && !isFormData) {
            // Only stringify if not URLSearchParams
            reqBody = JSON.stringify(reqBody);
        }

        // Combine base URL (if relative path) or use absolute path
        const fullUrl = `${this.baseUrl}/${path.replace(/^\//, '')}${queryString}`;

        // Set the correct content type if it's form data
        const contentType = isFormData ? 'application/x-www-form-urlencoded; charset=UTF-8' : 'application/json';

        const headers = {
            'content-type': contentType,
            'x-request-id': uuidv4(),
            ...options.headers,
            ...additionalHeaders,
        };

        // Create AbortController for this request
        const abortController = new AbortController();
        this.activeRequests.add(abortController);

        try {
            const response = await fetch(fullUrl, {
                method,
                headers,
                body: reqBody,
                credentials: 'include',
                mode: 'cors',
                signal: abortController.signal,
            });
            
            // Remove from active requests on completion
            this.activeRequests.delete(abortController);

            if (!response.ok) {
            if (response.status === 401) {
                // For 401 errors, show notification and redirect to login
                console.warn('Base HTTP Client: 401 error - handling session expiration');
                const { handle401Error } = use401Handler();
                handle401Error('accessing the API').catch(console.error);
                
                // Import router dynamically to avoid circular dependencies
                import('@/router').then(({ default: router }) => {
                    router.push('/login');
                });
            }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

            const responseData = await response.json();

            // Record the API response
            const functionName = new Error().stack?.split('\n')[2]?.match(/at\s+(\w+)/)?.[1] || 'unknown';
            recordApiResponse(functionName, fullUrl, method, responseData);

            return responseData;
        } catch (error) {
            // Remove from active requests on error
            this.activeRequests.delete(abortController);
            
            // Check if request was aborted
            if (error instanceof Error && error.name === 'AbortError') {
                console.debug('Request aborted:', fullUrl);
                throw new Error('Request cancelled');
            }
            
            throw error;
        }
    }

    async get<T>(path: string, params?: Record<string, string>, headers?: Record<string, string>): Promise<T> {
        return this.request<T>('GET', path, { params, headers });
    }

    async post<T>(
        path: string,
        body: any,
        params?: Record<string, string>,
        headers?: Record<string, string>
    ): Promise<T> {
        return this.request<T>('POST', path, { body, params, headers });
    }

    async put<T>(
        path: string,
        body: any,
        params?: Record<string, string>,
        headers?: Record<string, string>
    ): Promise<T> {
        return this.request<T>('PUT', path, { body, params, headers });
    }

    async patch<T>(
        path: string,
        body: any,
        params?: Record<string, string>,
        headers?: Record<string, string>
    ): Promise<T> {
        return this.request<T>('PATCH', path, { body, params, headers });
    }

    async delete<T>(path: string, params?: Record<string, string>, headers?: Record<string, string>): Promise<T> {
        return this.request<T>('DELETE', path, { params, headers });
    }

    get url(): string {
        return this.baseUrl;
    }
}
